/* eslint-disable max-params */
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { CommandContribution, CommandRegistry } from '@patsnap/tahiti-core';
import { Message } from '@patsnap/tahiti-core';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import { RequestFeedbackService } from '../request-feedback-service';
import { FeedbackService, type Feedback } from '../feedback-service';
import { I18nService } from '@telecom/shared/services/i18n';

interface FeedbackOptions {
    /**
     * 反馈类型
     * @description 用于区分不同的模块反馈类型
     */
    type?: string;
}

@injectable()
export class FeedbackCommandContribution implements CommandContribution {
    constructor(
        @inject(RequestFeedbackService)
        private readonly _requestFeedbackService: RequestFeedbackService,
        @inject(FeedbackService)
        private readonly _feedbackService: FeedbackService,
        @inject(Message)
        private readonly _message: Message,
        @inject(CurrentTaskService)
        private readonly _currentTaskService: CurrentTaskService,
        @inject(I18nService)
        private readonly _i18nService: I18nService,
    ) {}

    private _handleLike(options?: FeedbackOptions): Promise<void> {
        return this._sendFeedback({
            type: `${options?.type || this._currentTaskService.taskType}-T`,
            comment: '',
            taskId: this._currentTaskService.taskId,
        });
    }

    private async _handleFeedback(options?: FeedbackOptions): Promise<void> {
        const feedback = await this._requestFeedbackService.open();
        return this._sendFeedback({
            type: `${options?.type || this._currentTaskService.taskType}-F`,
            comment: feedback,
            taskId: this._currentTaskService.taskId,
        });
    }

    private _sendFeedback(feedback: Feedback): Promise<void> {
        return this._feedbackService.save(feedback)
            .finally(() => {
                const message = this._i18nService.t('drafting.feedback.Success');
                this._message.success({
                    message,
                });
            });
    }

    registerCommands(commands: CommandRegistry): void {
        commands.registerCommand({
            id: 'feedback:open',
        }, {
            execute: async (options: FeedbackOptions) => {
                await this._handleFeedback(options);
            },
        });

        commands.registerCommand({
            id: 'feedback:like',
        }, {
            execute: (options: FeedbackOptions) => {
                return this._handleLike(options);
            },
        });
    }
}

declare module '@patsnap/tahiti-core' {
    export interface CommandService {
        /**
         * 打开反馈对话框
         * @param commandId - 命令ID
         * @param options - 反馈选项
         */
        executeCommand(commandId: 'feedback:open', options: FeedbackOptions): Promise<void>;
        /**
         * 点赞
         * @param commandId - 命令ID
         */
        executeCommand(commandId: 'feedback:like', options: FeedbackOptions): Promise<void>;
    }
}
